import { Link, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { MessageCircle, ShoppingBag, Mail } from "lucide-react";
import { Header } from "./Header";
import { useStore } from "../../context/StoreContext";
import { waLink, money } from "../../lib/shop";

const FOOT_LINKS = [
  ["Shop", "/shop"], ["New Arrivals", "/new-arrivals"], ["Men", "/men"], ["Women", "/women"], ["Sale", "/sale"],
  ["Track Order", "/track-order"], ["Shipping & Delivery", "/shipping"], ["Returns & Refunds", "/returns"],
  ["FAQ", "/faq"], ["Contact", "/contact"], ["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"],
];

function Footer() {
  const { settings } = useStore();
  const s = settings || {};
  const socials = Object.entries(s.socials || {}).filter(([, v]) => v);
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#070708] pb-28 lg:pb-10">
      <div className="container-x pt-16 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <img src="/logo-512.webp" alt="Mwezzy Footwear" className="h-28 w-28 -ml-3" />
          <p className="font-display text-4xl mt-2 text-gold-gradient">STEP DIFFERENT.</p>
          <p className="text-sm text-zinc-400 mt-3 max-w-xs">South African online footwear store. Orders delivered nationwide by courier.</p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-2 gap-x-6 gap-y-3">
          {FOOT_LINKS.map(([l, to]) => (
            <Link key={to} to={to} className="text-sm text-zinc-300 hover:text-[#d4af37]" style={{ transition: "color .2s" }}
              data-testid={`footer-link-${to.slice(1)}`}>{l}</Link>
          ))}
        </div>
        <div className="lg:col-span-3 space-y-3 text-sm">
          <p className="eyebrow">Get in touch</p>
          <a href={`mailto:${s.contact_email}`} className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="footer-email"><Mail size={16} /> Email: {s.contact_email}</a>
          <a href={waLink(s.whatsapp_number || "", s.whatsapp_message)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="footer-whatsapp"><MessageCircle size={16} /> WhatsApp: {s.whatsapp_number}</a>
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-2 pt-2" data-testid="footer-socials">
              <span className="text-xs text-zinc-300 w-full">@mwezzyfootwear</span>
              {socials.map(([k, v]) => <a key={k} href={v} target="_blank" rel="noreferrer" data-testid={`footer-social-${k}`} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#d4af37]">{k === "x" ? "X" : k}</a>)}
            </div>
          )}
        </div>
      </div>
      <div className="container-x mt-14 pt-6 border-t border-white/10 text-xs text-zinc-500 flex flex-col sm:flex-row justify-between gap-2">
        <p data-testid="footer-copyright">© Mwezzy Footwear. All Rights Reserved.</p>
        <p>Online store · Courier delivery across South Africa</p>
      </div>
    </footer>
  );
}

function FloatingActions() {
  const { settings, cartCount, cart } = useStore();
  const { pathname } = useLocation();
  const showCart = cartCount > 0 && !["/cart", "/checkout"].includes(pathname);
  const subtotal = cart.reduce((s, x) => s + x.price * x.qty, 0);
  return (
    <>
      <a href={waLink(settings?.whatsapp_number || "+27 61 516 4287", settings?.whatsapp_message)} target="_blank" rel="noreferrer"
        data-testid="whatsapp-float-button" aria-label="Chat on WhatsApp"
        className={`fixed right-4 sm:right-6 z-40 bg-[#25D366] text-white rounded-full shadow-2xl p-4 hover:scale-110 ${showCart ? "bottom-24 lg:bottom-6" : "bottom-6"}`}
        style={{ transition: "transform .3s, bottom .3s" }}>
        <MessageCircle size={26} />
      </a>
      {showCart && (
        <Link to="/cart" data-testid="mobile-sticky-cart" className="lg:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-[#0a0a0c]/95 backdrop-blur border-t border-white/10">
          <span className="btn-gold w-full justify-between"><span className="flex items-center gap-2"><ShoppingBag size={18} /> View cart ({cartCount})</span><span>{money(subtotal)}</span></span>
        </Link>
      )}
    </>
  );
}

export function Layout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <FloatingActions />
    </div>
  );
}
